/* eslint-disable @typescript-eslint/naming-convention */
import {
  CancelToken,
  getTempUrl,
  getUploadToken,
  getVideoUploadToken,
  uploadFile,
} from 'src/model/upload-cos';
// @ts-ignore
import COS from 'cos-js-sdk-v5';

export interface UploadData {
  contentType: string;
  file: File;
}

export const utoa = (str: string) => window.btoa(unescape(encodeURIComponent(str)));

export const base64ToFile = (base64Str: any, name: string) => {
  const binary = atob(base64Str.split(',')[1]);
  const [temp] = base64Str.split(',');
  const [, mime] = temp?.match(/:(.*?);/) ?? [];
  const array = [];
  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i));
  }
  const fileData = new Blob([new Uint8Array(array)], {
    type: mime,
  });
  const file = new File([fileData], name, { type: mime });
  return file;
};

let cosInstance: any = null;
let taskId: any = null;
export const uploadVideoToCOS = async (file: File, feature: string, isCancel = false) => {
  if (isCancel) {
    cosInstance?.cancelTask(taskId);
    return;
  }
  try {
    return new Promise((resolve, reject) => {
      getVideoUploadToken({
        business_id: feature,
        doc_suffix: '.mp4',
      }).then((res) => {
        const {
          temp_secret_id: TmpSecretId,
          temp_secret_key: TmpSecretKey,
          session_token: XCosSecurityToken,
          start_time: StartTime,
          expire_time: ExpiredTime,
          // bucket_url: BucketUrl,
          bucket: Bucket,
          region: Region,
          key: Key,
        } = res;

        // 这里请求获取到token，调用上传cos接口
        cosInstance = new COS({
          getAuthorization: (_: any, callback: any) => {
            callback({
              TmpSecretId,
              TmpSecretKey,
              XCosSecurityToken,
              // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
              StartTime: Number(StartTime), // 时间戳，单位秒，如：1580000000
              ExpiredTime: Number(ExpiredTime), // 时间戳，单位秒，如：1580000900
            });
          },
        });
        cosInstance.uploadFile(
          {
            Bucket,
            Region,
            Key,
            Body: file,
            SliceSize: 1024 * 1024 * 10,
            onTaskReady: (id: any) => {
              taskId = id;
            },
          },
          (err: any, data: any) => {
            if (String(data?.statusCode) === '200') {
              getCosUrl(Key, '0', feature).then(
                (res) => {
                  resolve({
                    cosId: Key,
                    cosUrl: res || '',
                  });
                },
                () => {
                  reject(err);
                },
              );
            }
            taskId = null;
          },
        );
      });
    });
  } catch (e) {
    console.log('uploadCorpLogo error', e);
    return Promise.reject(e);
  }
};

export const cancelVideoUpload = () => {
  cosInstance?.cancelTask(taskId);
};

export const uploadToCos = async (
  file: File,
  preview?: boolean,
  onProgress?: (data: any) => void,
  cancelToken?: CancelToken,
  feature?: string,
) => {
  try {
    if (file.type.startsWith('video/mp4')) {
      try {
        return await uploadVideoToCOS(file, feature || 'wemeet_webinar');
      } catch (e) {
        return Promise.reject(e);
      }
    }
    // 获取token
    const {
      authorization,
      url,
      cos_id: cosId,
    } = await getUploadToken({
      content_type: file.type,
      feature: feature || 'activity_image', // 非图片资源传activity_resource
    });
    // 上传文件
    await uploadFile({
      file,
      authorization,
      url: decodeURIComponent(url),
      onUploadProgress: onProgress,
      cancelToken,
    });
    // 获取临时url
    const cosUrl = preview ? await getCosUrl(cosId, 'origin', feature) : '';
    return {
      cosId,
      cosUrl,
    };
  } catch (err) {
    return Promise.reject(err);
  }
};

/**
 * 获取cos的临时url
 * @param cosId {string} cosId
 * @param imageType {string} 图片尺寸，'1' | '2' | '3'
 * @param feature
 * @returns
 */
export const getCosUrl = (cosId: string, imageType: string, feature?: string) => {
  const postData = {
    cos_id: utoa(cosId),
    feature: feature || 'activity_image',
    image_type: imageType,
    check_type: '2',
  };
  return new Promise<string>((resolve, reject) => {
    getTempUrl(postData).then(
      (url) => {
        resolve(decodeURIComponent(url));
      },
      (err) => {
        reject(err);
      },
    );
  });
};

export const getVideoCoverUrl = async (cos_id: string) => {
  if (cos_id === '') return '';
  try {
    // cosId存在，则根据cosId获取封面url链接
    const afterVideoCosId = `${cos_id.slice(0, -4)}_0.jpg`;

    const coverUrl = await getCosUrl(afterVideoCosId, '2', 'wemeet_webinar');
    if (!coverUrl) {
      await Promise.reject({ message: '视频封面地址查询失败' });
    }
    // 视频上传成功之后，封面不一定马上就生成并取到，因此需要下面的兼容逻辑处理：preLoadImg：最多支持10次、间隔500ms
    const result: any = await preLoadImg({ src: coverUrl });
    if (String(result?.code) === '-1') {
      await Promise.reject({ message: '封面加载超过10次加载未成功' });
    }
    return coverUrl;
  } catch (e) {
    console.error('get cos url error', e);
  }
};

interface LoadImgProps {
  src: string;
  maxLoopCount?: number;
  interval?: number;
}

// img标签加载异常，支持多次加载
export const preLoadImg = async ({ src, maxLoopCount = 10, interval = 500 }: LoadImgProps) => {
  const createImgToLoad = () =>
    new Promise((resolve) => {
      const img = document.createElement('img');
      img.src = src;
      img.onload = () => resolve({ code: 0 });
      img.onerror = () => resolve({ code: -1 });
    });
  const loop: any = async () => {
    if (maxLoopCount < 0) {
      return Promise.reject({ code: -1 });
    }
    // eslint-disable-next-line no-param-reassign
    maxLoopCount = maxLoopCount - 1;

    // 查询结果
    let result: any = {};
    try {
      result = await createImgToLoad();
    } catch (e) {
      console.log(e);
      return Promise.reject({ code: -1 });
    }

    if (String(result?.code) === '0') {
      return result;
    }

    await new Promise((resolve) => {
      window.setTimeout(() => resolve(1), interval);
    });

    return loop();
  };

  return loop();
};

const checkFile = (list: File[], maxSize = Infinity, accept?: string) => {
  for (const item of list) {
    if (item.size > maxSize) {
      return {
        code: 1,
        reason: 'too large',
        file: item,
      };
    }
    if (accept !== undefined && !accept.includes(item.type)) {
      return {
        code: 2,
        reason: 'unaccept',
        file: item,
      };
    }
  }
  return true;
};

let inputRef: HTMLInputElement | null = null;

export const chooseUploadFile = async (multiple = false, maxSize?: number, accept?: string) => {
  let input: HTMLInputElement;
  if (inputRef) {
    input = inputRef;
  } else {
    input = document.createElement('input');
    document.body.append(input);
    inputRef = input;
  }
  input.type = 'file';
  input.multiple = multiple;
  if (accept !== undefined) input.accept = accept;
  input.style.visibility = 'hidden';
  input.value = '';
  input.click();
  return new Promise<File[]>((resolve, reject) => {
    input.onchange = () => {
      if (input.files?.length) {
        const list = Array.from(input.files); // 转成数组，files
        const checkResult = checkFile(list, maxSize, accept); // 文件类型大小是否符合
        if (checkResult === true) {
          resolve(list);
        } else {
          reject(checkResult);
        }
      }
    };
  });
};
